// #tag: HTTP API CLIENT AND STREAM UTILITY
// #purpose: Standardizes fetch requests, JWT/cookie credentials, error parsing, and binary download streams with progress tracking.
const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

// #tag: API ERROR CLASS
// #purpose: Custom error representation wrapping HTTP status codes and API error response payloads.
export class ApiError extends Error {
  status: number;
  data?: Record<string, unknown>;

  constructor(message: string, status: number, data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

function buildHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// #tag: GENERIC HTTP FETCH REQUEST WRAPPER
// #purpose: Dispatches HTTP requests with automatic header construction and error handling.
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { ...buildHeaders(), ...init?.headers },
      credentials: 'include',
    });
  } catch {
    throw new ApiError('Network error — is the API server running?', 0);
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let body: Record<string, unknown> | undefined;
    try {
      body = await res.json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      /* no JSON */
    }
    throw new ApiError(message, res.status, body);
  }

  return res.json() as Promise<T>;
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
}

export function apiDelete<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

export async function fetchBlob(path: string): Promise<Blob> {
  return fetchBlobWithProgress(path);
}

// #tag: BINARY BLOB STREAM DOWNLOADER
// #purpose: Streams binary data over HTTP chunk-by-chunk and broadcasts numerical download progress percentage.
export async function fetchBlobWithProgress(
  path: string,
  onProgress?: (percent: number) => void,
): Promise<Blob> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { headers, credentials: 'include' });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.clone().json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      /* no JSON */
    }
    throw new ApiError(message, res.status);
  }

  const contentType = res.headers.get('Content-Type') || 'application/octet-stream';
  const contentLength = Number(res.headers.get('Content-Length') || 0);

  if (!res.body || !Number.isFinite(contentLength) || contentLength <= 0) {
    onProgress?.(8);
    const blob = await res.blob();
    onProgress?.(100);
    return blob;
  }

  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let received = 0;

  onProgress?.(0);
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onProgress?.(Math.min(99, Math.round((received / contentLength) * 100)));
  }

  onProgress?.(100);
  return new Blob(chunks, { type: contentType });
}
